import { useContext } from 'react';
import { RefreshCwIcon, PresentationIcon } from 'lucide-react';
import { Outlet } from 'react-router';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { PageAccessCtx } from '@/hooks/page-access-context';

/**
 * The page remounts when the access it was loaded for changes, so everything it fetched,
 * lists and permissions alike, loads again; the shell around it, Sean's draft included, stays.
 * After a change in another tab the page waits behind a notice, keeping unsaved work.
 */
export function PageOutlet() {
  const access = useContext(PageAccessCtx);
  if (access === null) return <Outlet />;

  return (
    <>
      {access.pageIsStale ? (
        <Alert className="mb-6 border-accent/30 bg-accent/5">
          <PresentationIcon className="text-accent" />
          <AlertTitle>
            {access.pageDemoMode ? 'Demo mode ended in another tab' : 'Demo mode started in another tab'}
          </AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="min-w-0 flex-1 basis-60">
              This page still shows what it loaded before. Reloading it discards unsaved changes here.
            </span>
            <Button size="sm" variant="outline" className="shrink-0" onClick={access.reloadPage}>
              <RefreshCwIcon data-icon="inline-start" />
              Reload page
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}
      <Outlet key={access.pageDemoMode ? 'demo' : 'full'} />
    </>
  );
}
